import { Context, useRouter, useTranslation } from '@obvio/app'
import { useUi } from '@obvio/template'
import { createContext, useContext, useEffect } from 'react'

import { Footer } from './Footer'
import { InlineBookCalendar } from './InlineBookCalendar/InlineBookCalendar'
import { JsonLd } from './JsonLd'
import { Nav } from './Nav/Nav'
import { StyledCart } from './Store/StoreLayout'
import { StoreNav } from './Store/StoreNav'
import { heroSectionStore } from '@/utils/heroSectionStore'
import { hotelJsonLd } from '@/utils/jsonLd'
import { toSiteLocale } from '@/utils/seo'

import type { AllowUndefinedForOptional } from '@obvio/utils'
import type { ReactElement, ReactNode } from 'react'

export type LayoutType = 'Sklep' | 'Główny' | 'Kontakt' | 'Informacje'

type LayoutProps = AllowUndefinedForOptional<{
  children: ReactNode
  type: LayoutType
  hideCalendar?: boolean
}>

const LayoutContext = createContext<LayoutType>('Główny')

export function useLayoutType(): LayoutType {
  return useContext(LayoutContext)
}

function LayoutContent({
  children,
  type,
  hideCalendar,
}: LayoutProps): ReactElement {
  const [{ calendar, hero }] = heroSectionStore('calendar', 'hero')

  const isStore = type === 'Sklep'
  const isInfo = type === 'Informacje'
  return (
    <LayoutContext.Provider value={type}>
      {isStore ? <StoreNav /> : isInfo ? null : <Nav />}
      {children}
      {!isInfo && <Footer />}
      {hideCalendar
        ? null
        : calendar &&
          !hero &&
          type !== 'Sklep' && <InlineBookCalendar fixedBar />}
      {isStore && (
        <Context name="ecommerce" render="div">
          <StyledCart />
        </Context>
      )}
    </LayoutContext.Provider>
  )
}

export function Layout({
  children,
  hideCalendar,
  type,
}: LayoutProps): ReactElement {
  const { asPath } = useRouter()
  const { locale } = useTranslation()
  const [, dispatch] = useUi()

  useEffect(() => {
    dispatch('CLOSE_ALL_PANELS')
  }, [dispatch, asPath])

  return (
    <>
      <JsonLd id="hotel" data={hotelJsonLd(toSiteLocale(locale))} />
      <LayoutContent type={type} hideCalendar={hideCalendar}>
        {children}
      </LayoutContent>
    </>
  )
}
